/**
 * Fixed-DOCX tokenizer — the run-aware core of the "mark a variable" step.
 *
 * Problem (OOXML run-splitting): in `word/document.xml` a single visible phrase is
 * usually stored across several `<w:r><w:t>…</w:t></w:r>` runs, and Word may split
 * even a single word across runs. So a naive find/replace in the XML is unreliable,
 * and a browser DOM offset from the rendered doc is unreliable too.
 *
 * Strategy: flatten every `<w:t>` into one visible string `V` (recording which DOM
 * node each character came from), locate the admin's selected text in `V` by string
 * + surrounding context (run boundaries are irrelevant once flattened), then inject a
 * single contiguous `{name}` token into the FIRST run of the span (inheriting its
 * `<w:rPr>` formatting) and blank the rest of the span. The token is contiguous in one
 * run, so the merge engine (easy-template-x) finds it trivially at generate time.
 *
 * This is the BROWSER port of the old app's Node tokenizer: it uses `pizzip` +
 * `@xmldom/xmldom` (both browser-safe) and works with `Uint8Array` instead of Node
 * `Buffer`. Pure and stateless — no network/DB deps.
 */

import PizZip from 'pizzip';
import { DOMParser, XMLSerializer, type Node as XmlNode } from '@xmldom/xmldom';
import { ERR_AMBIGUOUS, ERR_EMPTY_SELECTION, ERR_NOT_FOUND, locateText } from './text-locate';

export const TOKEN_START = '{';
export const TOKEN_END = '}';

/** Build the literal token injected for a scalar variable, e.g. "effective_date" -> "{effective_date}". */
export function makeToken(name: string): string {
  return `${TOKEN_START}${name}${TOKEN_END}`;
}

const NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
export const DOCUMENT_PART = 'word/document.xml';

export interface ScalarSelectionInput {
  /** Variable identifier — must match NAME_RE and be unique. */
  name: string;
  /** The exact text the admin selected in the rendered document. */
  selectedText: string;
  /** A few chars immediately before the selection (for disambiguation). */
  contextBefore?: string;
  /** A few chars immediately after the selection (for disambiguation). */
  contextAfter?: string;
  /** Coarse paragraph anchor from the renderer (stored; not load-bearing). */
  paragraphIndex?: number | null;
  /**
   * Which occurrence of `selectedText` the admin marked, and how many the rendered document
   * held at that moment. Together these break ties that no amount of context can — see
   * {@link LocateOptions}. Absent on selections marked before this was recorded.
   */
  occurrenceIndex?: number | null;
  occurrenceCount?: number | null;
}

export interface ResolvedVariable {
  name: string;
  token: string;
  sourceSpan: {
    docPart: string;
    paragraphIndex: number | null;
    originalText: string;
    contextBefore: string;
    contextAfter: string;
    occurrenceIndex?: number | null;
    occurrenceCount?: number | null;
  };
}

export interface TokenizeFailure {
  name: string;
  selectedText: string;
  reason: string;
}

export interface TokenizeResult {
  /** The tokenized .docx bytes, or null if any selection failed (all-or-nothing). */
  buffer: Uint8Array | null;
  resolved: ResolvedVariable[];
  failures: TokenizeFailure[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Internal text model
// ---------------------------------------------------------------------------

export interface Segment {
  /** The <w:t> element this text came from, or null for synthetic whitespace (tab/break/paragraph gap). */
  node: Element | null;
  /** Start offset of this segment's text within V. */
  vStart: number;
  text: string;
  paragraphIndex: number;
  /**
   * Twin <w:t> elements holding byte-identical text in the sibling `mc:AlternateContent`
   * branch (see {@link buildTextModel}). Only one branch is flattened into V, so every edit
   * must be replayed here too or the copy Word actually reads would keep the stale text.
   */
  mirrors?: Element[];
}

export interface TextModel {
  V: string;
  segments: Segment[];
}

/** Concatenated text of a <w:t> element (its child text nodes). */
function readWtText(node: Element): string {
  let s = '';
  let c = node.firstChild;
  while (c) {
    if (c.nodeType === 3 /* TEXT_NODE */) s += (c as unknown as { data: string }).data;
    c = c.nextSibling;
  }
  return s;
}

/** Replace a <w:t> element's text and mark it space-preserving (so leading/trailing spaces survive). */
export function setWtText(node: Element, doc: Document, text: string): void {
  while (node.firstChild) node.removeChild(node.firstChild);
  node.appendChild(doc.createTextNode(text));
  node.setAttribute('xml:space', 'preserve');
}

/** Direct element children of `node` whose local (prefix-stripped) name matches. */
function childrenByLocalName(node: Element, localName: string): Element[] {
  const out: Element[] = [];
  let c = node.firstChild;
  while (c) {
    if (c.nodeType === 1 /* ELEMENT_NODE */) {
      const el = c as unknown as Element;
      if ((el.localName || el.nodeName) === localName) out.push(el);
    }
    c = c.nextSibling;
  }
  return out;
}

/** Every <w:t> under `root`, in document order. */
function collectWtNodes(root: Element): Element[] {
  const out: Element[] = [];
  const rec = (n: Element) => {
    if (n.nodeName === 'w:t') {
      out.push(n);
      return;
    }
    let c = n.firstChild;
    while (c) {
      if (c.nodeType === 1 /* ELEMENT_NODE */) rec(c as unknown as Element);
      c = c.nextSibling;
    }
  };
  rec(root);
  return out;
}

/**
 * Pair each <w:t> in the rendered `mc:AlternateContent` branch with its counterpart in the
 * other branch, so an edit to one can be mirrored into the other.
 *
 * Returns null unless the two branches carry a byte-identical run sequence — the only case
 * where the correspondence is unambiguous. A shape whose branches genuinely differ is left
 * alone rather than guessed at.
 */
function buildMirrorMap(rendered: Element, other: Element | undefined): Map<Element, Element> | null {
  if (!other) return null;
  const a = collectWtNodes(rendered);
  const b = collectWtNodes(other);
  if (a.length === 0 || a.length !== b.length) return null;
  for (let i = 0; i < a.length; i++) {
    if (readWtText(a[i]) !== readWtText(b[i])) return null;
  }
  const map = new Map<Element, Element>();
  for (let i = 0; i < a.length; i++) map.set(a[i], b[i]);
  return map;
}

/**
 * Walk the document in order, flattening every <w:t> into V while tracking paragraph
 * index and inserting synthetic whitespace for <w:tab>, <w:br>/<w:cr>, and paragraph
 * gaps so V's spacing roughly matches what the admin saw rendered.
 *
 * `mc:AlternateContent` (how Word stores a shape or text box) holds the SAME visible text
 * twice — once under `mc:Choice` for modern readers, once under `mc:Fallback` as legacy
 * VML. Walking both put every shape's text into V twice with byte-identical surroundings,
 * which made the context scores tie and reported perfectly ordinary text as "appears
 * multiple times". Only the branch docx-preview renders is flattened; its runs carry
 * `mirrors` pointing at the twin runs so edits still reach both copies.
 */
export function buildTextModel(doc: Document): TextModel {
  const root = doc.getElementsByTagName('w:body')[0] || doc.documentElement;
  const segments: Segment[] = [];
  let V = '';
  let paraIndex = -1;
  let mirrorMap: Map<Element, Element> | null = null;

  const pushPhantom = (ch: string) => {
    segments.push({ node: null, vStart: V.length, text: ch, paragraphIndex: paraIndex });
    V += ch;
  };

  const walkChildren = (node: Element) => {
    let child = node.firstChild;
    while (child) {
      if (child.nodeType === 1 /* ELEMENT_NODE */) walk(child as unknown as Element);
      child = child.nextSibling;
    }
  };

  const walk = (node: Element) => {
    const tag = node.nodeName;
    if (tag === 'w:p') {
      paraIndex++;
      if (V.length > 0 && !V.endsWith('\n')) pushPhantom('\n');
    }
    if (tag === 'w:t') {
      const text = readWtText(node);
      const mirror = mirrorMap?.get(node);
      segments.push({
        node,
        vStart: V.length,
        text,
        paragraphIndex: paraIndex,
        mirrors: mirror ? [mirror] : undefined,
      });
      V += text;
      return; // do not recurse into <w:t>
    }
    if (tag === 'w:tab') {
      pushPhantom('\t');
      return;
    }
    if (tag === 'w:br' || tag === 'w:cr') {
      pushPhantom('\n');
      return;
    }
    if ((node.localName || tag) === 'AlternateContent') {
      // docx-preview declares no supported Choice namespaces (`supportedNamespaceURIs` is
      // empty), so it renders Fallback whenever one exists. Flattening the other branch
      // would mean the occurrence the admin marked in the preview and the occurrence we
      // tokenize are different copies of the same words.
      const fallback = childrenByLocalName(node, 'Fallback')[0];
      const choice = childrenByLocalName(node, 'Choice')[0];
      const rendered = fallback ?? choice;
      if (!rendered) return;
      const outer = mirrorMap;
      mirrorMap = buildMirrorMap(rendered, rendered === fallback ? choice : fallback);
      walkChildren(rendered);
      mirrorMap = outer;
      return;
    }
    walkChildren(node);
  };

  if (root) walk(root as unknown as Element);
  return { V, segments };
}

export interface Located {
  rawStart: number;
  rawEnd: number;
}

/**
 * Locate a selection in V — the STRICT policy over {@link locateText}: an ambiguous span is
 * an error, never a guess, because tokenizing (or editing) the wrong occurrence silently
 * corrupts the document.
 */
export function locate(model: TextModel, sel: ScalarSelectionInput): Located | { error: string } {
  const outcome = locateText(model.V, sel.selectedText, {
    contextBefore: sel.contextBefore,
    contextAfter: sel.contextAfter,
    occurrenceIndex: sel.occurrenceIndex,
    occurrenceCount: sel.occurrenceCount,
  });
  if (outcome.empty) return { error: ERR_EMPTY_SELECTION };
  if (!outcome.span) return { error: ERR_NOT_FOUND };
  if (outcome.ambiguous) return { error: ERR_AMBIGUOUS };
  return outcome.span;
}

export interface NodeEdit {
  node: Element;
  localStart: number;
  localEnd: number;
  replacement: string;
}

/**
 * Map a [rawStart, rawEnd) span in V to per-<w:t> edits: `replacement` into the first run,
 * blank the rest. Used both to inject a `{token}` (tokenizer) and to substitute edited text
 * (live editor) — in both cases the whole span collapses into the first covering run.
 */
export function spanToEdits(
  model: TextModel,
  rawStart: number,
  rawEnd: number,
  token: string
): NodeEdit[] | { error: string } {
  const overlap = model.segments.filter(
    (s) => s.node && s.vStart < rawEnd && s.vStart + s.text.length > rawStart
  );
  if (overlap.length === 0) {
    return { error: 'Selection does not map to editable text' };
  }
  return overlap.flatMap((s, i) => {
    const segStart = s.vStart;
    const segEnd = s.vStart + s.text.length;
    const localStart = Math.max(rawStart, segStart) - segStart;
    const localEnd = Math.min(rawEnd, segEnd) - segStart;
    const replacement = i === 0 ? token : '';
    const primary: NodeEdit = { node: s.node as Element, localStart, localEnd, replacement };
    // Mirrors hold identical text at identical offsets, so the same edit applies verbatim.
    const twins = (s.mirrors ?? []).map((node) => ({ node, localStart, localEnd, replacement }));
    return [primary, ...twins];
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function toUint8(input: Uint8Array | ArrayBuffer): Uint8Array {
  return input instanceof Uint8Array ? input : new Uint8Array(input);
}

/**
 * Open a .docx, parse `word/document.xml` into a DOM, and return both so callers can
 * mutate the DOM and re-zip via {@link serializeDocxDocument}. Strips Word's UTF-8 BOM
 * (and stray leading whitespace) which @xmldom/xmldom otherwise rejects.
 */
export function loadDocxDocument(input: Uint8Array | ArrayBuffer): {
  zip: PizZip;
  doc: Document;
} {
  const zip = new PizZip(toUint8(input));
  const docXmlFile = zip.file(DOCUMENT_PART);
  if (!docXmlFile) {
    throw new Error('Invalid .docx: missing word/document.xml');
  }
  let xml = docXmlFile.asText();
  if (xml.charCodeAt(0) === 0xfeff) xml = xml.slice(1); // drop UTF-8 BOM
  xml = xml.replace(/^\s+/, '');
  const doc = new DOMParser().parseFromString(xml, 'application/xml') as unknown as Document;
  return { zip, doc };
}

/** Serialize a mutated document DOM back into the zip and return the new .docx bytes. */
export function serializeDocxDocument(zip: PizZip, doc: Document): Uint8Array {
  let outXml = new XMLSerializer().serializeToString(doc as unknown as XmlNode);
  if (!outXml.startsWith('<?xml')) {
    outXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\r\n${outXml}`;
  }
  zip.file(DOCUMENT_PART, outXml);
  return zip.generate({ type: 'uint8array', compression: 'DEFLATE' }) as Uint8Array;
}

/** Validate variable names up front (identifier shape + uniqueness). Throws on violation. */
function validateNames(selections: ScalarSelectionInput[]): void {
  const seen = new Set<string>();
  for (const sel of selections) {
    if (!NAME_RE.test(sel.name)) {
      throw new Error(
        `Invalid variable name "${sel.name}" — use letters, numbers and underscores, starting with a letter or underscore.`
      );
    }
    if (seen.has(sel.name)) {
      throw new Error(`Duplicate variable name "${sel.name}".`);
    }
    seen.add(sel.name);
  }
}

/**
 * Tokenize a .docx: inject a `{name}` token at each selected span, preserving formatting.
 * All-or-nothing: if any selection cannot be located unambiguously, returns `buffer: null`
 * with `failures` and does not modify the document.
 */
export function tokenizeDocx(
  input: Uint8Array | ArrayBuffer,
  selections: ScalarSelectionInput[]
): TokenizeResult {
  validateNames(selections);

  // Word writes every XML part with a leading UTF-8 BOM (handled inside loadDocxDocument,
  // which @xmldom/xmldom would otherwise reject).
  const { zip, doc } = loadDocxDocument(input);

  const model = buildTextModel(doc);

  const warnings: string[] = [];
  if (model.V.includes(TOKEN_START) || model.V.includes(TOKEN_END)) {
    warnings.push(
      `The document already contains "${TOKEN_START}" or "${TOKEN_END}" characters, which may interfere with field substitution.`
    );
  }

  // Phase 1: locate every selection against the ORIGINAL V (before any edits).
  const resolved: ResolvedVariable[] = [];
  const failures: TokenizeFailure[] = [];
  const pendingEdits: NodeEdit[] = [];
  const spans: Array<{ start: number; end: number }> = [];

  for (const sel of selections) {
    const token = makeToken(sel.name);
    const loc = locate(model, sel);
    if ('error' in loc) {
      failures.push({ name: sel.name, selectedText: sel.selectedText, reason: loc.error });
      continue;
    }
    // Reject overlaps with already-accepted spans.
    const overlapsExisting = spans.some((s) => loc.rawStart < s.end && loc.rawEnd > s.start);
    if (overlapsExisting) {
      failures.push({
        name: sel.name,
        selectedText: sel.selectedText,
        reason: 'Selection overlaps another marked field.',
      });
      continue;
    }
    const edits = spanToEdits(model, loc.rawStart, loc.rawEnd, token);
    if ('error' in edits) {
      failures.push({ name: sel.name, selectedText: sel.selectedText, reason: edits.error });
      continue;
    }
    spans.push({ start: loc.rawStart, end: loc.rawEnd });
    pendingEdits.push(...edits);
    const seg = model.segments.find(
      (s) => s.node && s.vStart <= loc.rawStart && s.vStart + s.text.length > loc.rawStart
    );
    resolved.push({
      name: sel.name,
      token,
      sourceSpan: {
        docPart: 'document',
        paragraphIndex: seg ? seg.paragraphIndex : sel.paragraphIndex ?? null,
        originalText: sel.selectedText,
        contextBefore: sel.contextBefore ?? '',
        contextAfter: sel.contextAfter ?? '',
        occurrenceIndex: sel.occurrenceIndex ?? null,
        occurrenceCount: sel.occurrenceCount ?? null,
      },
    });
  }

  if (failures.length > 0) {
    return { buffer: null, resolved: [], failures, warnings };
  }

  // Phase 2: apply edits, then serialize and re-zip.
  applyNodeEdits(model, doc, pendingEdits);
  const buffer = serializeDocxDocument(zip, doc);

  return { buffer, resolved, failures, warnings };
}

/**
 * Apply a batch of per-run edits to the DOM in place. Groups edits by their <w:t> node and
 * rebuilds each node's text once from its ORIGINAL content, so multiple spans within one run
 * compose correctly. Throws on intra-run overlap (callers must keep spans disjoint).
 */
export function applyNodeEdits(model: TextModel, doc: Document, edits: NodeEdit[]): void {
  const byNode = new Map<Element, { original: string; edits: NodeEdit[] }>();
  const nodeOriginal = new Map<Element, string>();
  for (const seg of model.segments) {
    if (!seg.node) continue;
    if (!nodeOriginal.has(seg.node)) nodeOriginal.set(seg.node, seg.text);
    // Mirror twins never appear in `segments` (only one branch is flattened), but their
    // text is verified identical to the segment's, so it is their original too.
    for (const twin of seg.mirrors ?? []) {
      if (!nodeOriginal.has(twin)) nodeOriginal.set(twin, seg.text);
    }
  }
  for (const edit of edits) {
    const entry = byNode.get(edit.node) ?? {
      original: nodeOriginal.get(edit.node) ?? '',
      edits: [],
    };
    entry.edits.push(edit);
    byNode.set(edit.node, entry);
  }

  for (const [node, { original, edits: nodeEdits }] of byNode) {
    nodeEdits.sort((a, b) => a.localStart - b.localStart);
    // Guard against intra-node overlaps (shouldn't happen given span-disjoint check).
    for (let i = 1; i < nodeEdits.length; i++) {
      if (nodeEdits[i].localStart < nodeEdits[i - 1].localEnd) {
        throw new Error('Internal error: overlapping edits within a run');
      }
    }
    let out = '';
    let cursor = 0;
    for (const e of nodeEdits) {
      out += original.slice(cursor, e.localStart) + e.replacement;
      cursor = e.localEnd;
    }
    out += original.slice(cursor);
    setWtText(node, doc, out);
  }
}
